import React from "react";
import { Message } from "../types";
import { useSettings } from "../contexts/SettingsContext";
import { CheckCircle2 } from "lucide-react";

interface Props {
  message: Message;
}

interface FlexibleMetadata {
  [key: string]: any;
}

const AgenticResponse: React.FC<Props> = ({ message }) => {
  const { debugMode } = useSettings();
  const extractMetadata = () => {
    const metadata = (message.metadata || {}) as FlexibleMetadata;

    const findValueByKey = (
      obj: any,
      targetKey: string,
      visited = new Set()
    ): any => {
      if (!obj || typeof obj !== "object" || visited.has(obj)) return null;
      visited.add(obj);
      if (obj[targetKey] !== undefined && obj[targetKey] !== null)
        return obj[targetKey];
      for (const [key, value] of Object.entries(obj)) {
        if (typeof value === "object" && value !== null) {
          const found = findValueByKey(value, targetKey, visited);
          if (found !== null && found !== undefined) return found;
        }
      }
      return null;
    };

    const sources = [
      metadata.meta,
      metadata,
      metadata.result,
      metadata.action,
      metadata.details,
    ].filter(Boolean);

    const extractField = (fieldNames: string[]): any => {
      for (const source of sources) {
        for (const fieldName of fieldNames) {
          const value = findValueByKey(source, fieldName);
          if (value !== null && value !== undefined && value !== "")
            return value;
        }
      }
      return null;
    };

    return {
      topic: extractField(["topic", "actionTopic", "category", "domain"]),
      action: extractField([
        "action",
        "actionName",
        "action_name",
        "name",
        "operation",
        "method",
      ]),
      actionKey: extractField(["actionKey", "action_key", "key", "identifier"]),
      provider: extractField([
        "provider",
        "channel",
        "service",
        "system",
        "source",
      ]),
      flowName: extractField([
        "flowApiName",
        "flowName",
        "flow_name",
        "processName",
        "workflowName",
      ]),
      duration: extractField([
        "durationMs",
        "duration",
        "executionTime",
        "processingTime",
      ]),
      status: extractField(["ok", "success", "successful", "status", "state"]),
      inputs: extractField([
        "inputs",
        "input",
        "parameters",
        "params",
        "collected",
      ]),
      outputs: extractField([
        "output",
        "outputs",
        "result",
        "results",
        "response",
      ]),
      error: extractField([
        "error",
        "errorMessage",
        "err",
        "exception",
        "failure",
      ]),
      errorCode: extractField([
        "code",
        "errorCode",
        "error_code",
        "statusCode",
      ]),
      type: extractField(["type", "messageType", "resultType", "kind"]),
      rawMetadata: metadata,
    };
  };

  const meta = extractMetadata();

  const generateActionName = (): string => {
    const actionSources = [
      meta.action,
      meta.actionKey,
      meta.topic && meta.action ? `${meta.topic} ${meta.action}` : null,
      meta.flowName
        ? String(meta.flowName)
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/\b\w/g, (l) => l.toUpperCase())
        : null,
      meta.type === "agentic_action_result" ? "Agentic Action" : null,
      meta.type === "flow_execution" ? "Flow Execution" : null,
      "System Action",
    ];
    const result = actionSources.find(
      (source) => source && typeof source === "string" && String(source).trim()
    );
    return String(result || "Unknown Action");
  };

  const generateChannel = (): string => {
    const channelSources = [
      meta.provider,
      meta.flowName ? "salesforce.flow" : null,
      meta.outputs && typeof meta.outputs === "object" ? "api" : null,
      "system",
    ];
    const result = channelSources.find(
      (source) => source && typeof source === "string"
    );
    return String(result || "unknown");
  };

  const isSuccess = (): boolean => {
    if (meta.error || meta.errorCode) return false;
    if (
      meta.status === true ||
      meta.status === "success" ||
      meta.status === "completed"
    )
      return true;
    if (
      meta.status === false ||
      meta.status === "failed" ||
      meta.status === "error"
    )
      return false;
    if (
      meta.outputs &&
      typeof meta.outputs === "object" &&
      Object.keys(meta.outputs).length > 0
    )
      return true;
    return !meta.error;
  };

  // const formatSuccessData = (data: any): React.ReactNode => {
  //   if (!data || typeof data !== "object") return null;

  //   const keysToDisplay = [
  //     // "caseNumber",
  //     // "Case Number",
  //     "outputMessage",
  //     "Output Message",
  //   ];
  //   return (
  //     <div className="space-y-1 text-sm">
  //       {keysToDisplay.map((key) => {
  //         const value = data[key] ?? null;
  //         if (!value) return null;

  //         const label = key
  //           .replace(/([A-Z])/g, " $1")
  //           .replace(/_/g, " ")
  //           .replace(/\b\w/g, (l) => l.toUpperCase());
  //         return (
  //           <p key={key}>
  //             <strong>{label}:</strong> {String(value)}
  //           </p>
  //         );
  //       })}
  //     </div>
  //   );
  // };

  const formatSuccessData = (data: any): React.ReactNode => {
    if (!data || typeof data !== "object") return null;

    const keysToDisplay = ["outputMessage", "Output Message"];

    return (
      <div className="space-y-1 text-sm">
        {keysToDisplay.map((key) => {
          const value = data[key] ?? null;
          if (!value) return null;

          // Return only the message content without the label
          return (
            <p key={key} className="text-gray-200 text-base leading-relaxed">
              {String(value)}
            </p>
          );
        })}
      </div>
    );
  };

  const actionName = generateActionName();
  const channel = generateChannel();
  const success = isSuccess();
  const bgColor = success
    ? "bg-[#020617] border-[#334155]"
    : "bg-[#020617] border-red-500/30";

  return (
    <div
      className={`relative p-6 rounded-2xl border backdrop-blur-xl shadow-2xl transition-all duration-500 ${
        success
          ? "bg-gradient-to-br from-green-600/20 via-emerald-700/10 to-green-900/20 border-green-600/30"
          : "bg-gradient-to-br from-red-700/30 via-rose-800/20 to-red-900/40 border-red-500/40"
      }`}
    >
      {/* Animated Glow Border */}
      {success && (
        <div className="absolute inset-0 rounded-2xl border-2 border-green-500/20 animate-pulse blur-sm pointer-events-none" />
      )}

      {debugMode && (
        <>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white tracking-wide flex items-center gap-3">
              <span
                className={`relative flex h-3 w-3 ${
                  success ? "bg-green-400" : "bg-red-400"
                } rounded-full`}
              >
                <span
                  className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${
                    success
                      ? "animate-ping bg-green-400"
                      : "animate-ping bg-red-400"
                  }`}
                ></span>
              </span>
              Agentic Action Result
            </h3>
            {meta.duration && (
              <span className="text-xs text-gray-300 font-light italic">
                ⏱ {String(meta.duration)}ms
              </span>
            )}
          </div>

          {/* Metadata */}
          <div className="text-sm text-gray-100 mb-5 space-y-1">
            <p>
              <span className="text-gray-300">Action:</span>{" "}
              <strong className="text-white font-semibold">{actionName}</strong>
              {meta.flowName && (
                <span className="text-gray-400 ml-1">
                  (Flow: {String(meta.flowName)})
                </span>
              )}
              {channel && (
                <span className="text-gray-400 ml-1 italic">via {channel}</span>
              )}
            </p>
          </div>
        </>
      )}

      {/* Success Output */}
      {success && meta.outputs && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="text-green-400 h-5 w-5 animate-pulse" />
            <h4 className="text-base font-semibold text-green-200 tracking-wide">
              Operation Completed Successfully
            </h4>
          </div>
          <div className="text-green-50 !text-base leading-relaxed whitespace-pre-wrap">
            {formatSuccessData(meta.outputs)}
          </div>
        </div>
      )}

      {/* Error Output */}
      {!success && meta.error && (
        <div className="p-5 rounded-xl bg-gradient-to-br from-red-600/10 via-red-700/10 to-red-800/20 border border-red-500/30 shadow-inner">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-2 w-2 rounded-full bg-red-400 animate-pulse"></div>
            <h4 className="text-base font-semibold text-red-200 tracking-wide">
              Operation Failed
            </h4>
          </div>
          <p className="text-xs text-red-100">{String(meta.error)}</p>
          {meta.errorCode && (
            <p className="text-xs text-red-400 mt-1">
              Code: {String(meta.errorCode)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AgenticResponse;
